import { DollarSign, Leaf, TrendingUp, Zap } from "lucide-react";
import type { EnergyHistory } from "@spf/contracts";
import { Failed, Loading } from "../ViewState.js";
import { coverageNote, formatKwh, recordedSince } from "../../lib/energy.js";
import type { Async } from "../../lib/useAsync.js";

/**
 * The design's four headline cards, over what this system actually knows.
 *
 * The design's four are cumulative savings, self-sufficiency, avoided grid
 * cost and carbon abatement, each a hardcoded figure with a hardcoded
 * comparison beneath it ("+18.4% vs last year", "~420 trees planted").
 *
 * The first two carry energy delivered to the house, today and since records
 * began, integrated on the server from stored readings. Until slice 5 they
 * were the inverter's `eac_dischar` counters, which count only load served
 * from the grid and so fell to zero once solar carried the house
 * (docs/DECISIONS/0027). The other two are shown empty rather than dropped.
 * The avoided cost prices the solar share, which is its own slice now that
 * tariffs exist (docs/DECISIONS/0033); carbon needs an emissions factor.
 */
export function Highlights({
  today,
  lifetime,
  onRetry,
}: {
  today: Async<EnergyHistory>;
  lifetime: Async<EnergyHistory>;
  onRetry: () => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Highlight
        label="Delivered today"
        icon={<Zap size={16} aria-hidden="true" className="text-accent-ink" />}
      >
        <Delivered state={today} onRetry={onRetry} note="Worked out from today's readings." />
      </Highlight>
      <Highlight
        label="Delivered since records began"
        icon={<TrendingUp size={16} aria-hidden="true" className="text-accent-2-ink" />}
      >
        <Delivered
          state={lifetime}
          onRetry={onRetry}
          note={lifetime.kind === "ready" ? recordedSince(lifetime.value) : ""}
        />
      </Highlight>
      <Highlight
        label="Avoided grid cost"
        icon={<DollarSign size={16} aria-hidden="true" className="text-ink-muted" />}
      >
        <Figure
          value="—"
          note="What the panels saved is not priced yet. It comes in a later update."
        />
      </Highlight>
      <Highlight
        label="Carbon abatement"
        icon={<Leaf size={16} aria-hidden="true" className="text-ink-muted" />}
      >
        <Figure value="—" note="Needs an emissions factor for this grid. None is configured." />
      </Highlight>
    </div>
  );
}

function Delivered({
  state,
  note,
  onRetry,
}: {
  state: Async<EnergyHistory>;
  note: string;
  onRetry: () => void;
}) {
  if (state.kind === "loading") return <Loading label="Loading…" />;
  if (state.kind === "error") return <Failed message={state.message} onRetry={onRetry} />;
  const { totals } = state.value;
  const caveat = coverageNote(totals);
  return (
    <Figure
      value={formatKwh(totals.loadKwh)}
      note={caveat === null || totals.loadKwh === null ? note : `${note} ${caveat}.`}
    />
  );
}

function Figure({ value, note }: { value: string; note: string }) {
  return (
    <>
      <p className="tabular text-3xl font-bold text-ink">{value}</p>
      <p className="mt-2 text-xs text-ink-muted">{note}</p>
    </>
  );
}

function Highlight({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-card border border-line bg-card p-6">
      <div className="mb-3 flex items-center justify-between gap-2 text-xs text-ink-muted">
        <h3>{label}</h3>
        {icon}
      </div>
      {children}
    </section>
  );
}
